import { getDB, type PlayerGroup } from "./schema";
import type { Card, Pack, PackProgress, Player, Session } from "../domain/types";
import type { Language, ModeId, PackStatus } from "../domain/enums";

export type { PlayerGroup } from "./schema";

// ---------------------- packs / cards ----------------------

export async function getAllPacks(): Promise<Pack[]> {
  const db = await getDB();
  return db.getAll("packs");
}

export async function getPacksByMode(modeId: ModeId): Promise<Pack[]> {
  const db = await getDB();
  return db.getAllFromIndex("packs", "by_mode", modeId);
}

export async function getPackById(id: string): Promise<Pack | undefined> {
  const db = await getDB();
  return db.get("packs", id);
}

export async function getCardsByPackAndLanguage(
  packId: string,
  language: Language
): Promise<Card[]> {
  const db = await getDB();
  return db.getAllFromIndex("cards", "by_pack_lang", [packId, language]);
}

export async function getCardsByPack(packId: string): Promise<Card[]> {
  const db = await getDB();
  return db.getAllFromIndex("cards", "by_pack", packId);
}

export async function putCard(card: Card): Promise<void> {
  const db = await getDB();
  await db.put("cards", card);
}

export async function deleteCard(id: string): Promise<void> {
  const db = await getDB();
  await db.delete("cards", id);
}

export async function putPack(pack: Pack): Promise<void> {
  const db = await getDB();
  await db.put("packs", pack);
}

// ---------------------- pack progress ----------------------

export async function getPackProgress(packId: string): Promise<PackProgress | undefined> {
  const db = await getDB();
  return db.get("pack_progress", packId);
}

export async function setPackProgress(p: PackProgress): Promise<void> {
  const db = await getDB();
  await db.put("pack_progress", p);
}

export function deriveStatus(cardsSeen: number, totalCards: number): PackStatus {
  if (totalCards === 0) return "not_started";
  if (cardsSeen >= totalCards) return "completed";
  if (cardsSeen > 0) return "in_progress";
  return "not_started";
}

// ---------------------- sessions ----------------------

export async function putSession(s: Session): Promise<void> {
  const db = await getDB();
  await db.put("sessions", s);
}

export async function getSession(id: string): Promise<Session | undefined> {
  const db = await getDB();
  return db.get("sessions", id);
}

export async function getActiveSession(): Promise<Session | undefined> {
  const db = await getDB();
  const tx = db.transaction("sessions", "readonly");
  const idx = tx.store.index("by_startedAt");
  let cursor = await idx.openCursor(null, "prev");
  while (cursor) {
    if (cursor.value.endedAt == null) return cursor.value;
    cursor = await cursor.continue();
  }
  return undefined;
}

// ---------------------- meta ----------------------

export async function getMeta<T = unknown>(key: string): Promise<T | undefined> {
  const db = await getDB();
  const row = await db.get("meta", key);
  return row?.value as T | undefined;
}
export async function setMeta(key: string, value: unknown): Promise<void> {
  const db = await getDB();
  await db.put("meta", { key, value });
}

// ---------------------- bulk operations ----------------------

/**
 * Wipe seeded content but PRESERVE user-created packs and their cards.
 * User packs carry `userCreated: true`; their cards are matched by packId.
 * Without this carve-out, "Reset content" would destroy the host's own
 * authored decks every time we ship a seed bump.
 */
export async function clearContent(): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(["cards", "packs"], "readwrite");
  const packsStore = tx.objectStore("packs");
  const cardsStore = tx.objectStore("cards");
  const allPacks = await packsStore.getAll();
  const userPackIds = new Set(
    allPacks.filter((p) => p.userCreated).map((p) => p.id),
  );
  for (const p of allPacks) {
    if (!userPackIds.has(p.id)) await packsStore.delete(p.id);
  }
  let cursor = await cardsStore.openCursor();
  while (cursor) {
    if (!userPackIds.has(cursor.value.packId)) {
      await cursor.delete();
    }
    cursor = await cursor.continue();
  }
  await tx.done;
}

export async function bulkPutCards(cards: Card[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("cards", "readwrite");
  for (const c of cards) tx.store.put(c);
  await tx.done;
}

export async function bulkPutPacks(packs: Pack[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("packs", "readwrite");
  for (const p of packs) tx.store.put(p);
  await tx.done;
}

// ---------------------- player groups ----------------------

export async function listPlayerGroups(): Promise<PlayerGroup[]> {
  const db = await getDB();
  const all = await db.getAll("player_groups");
  return all.sort((a, b) => b.lastUsedAt - a.lastUsedAt);
}

export async function getPlayerGroup(id: string): Promise<PlayerGroup | undefined> {
  const db = await getDB();
  return db.get("player_groups", id);
}

export async function savePlayerGroup(args: {
  id?: string;
  name: string;
  players: Player[];
}): Promise<PlayerGroup> {
  const db = await getDB();
  const now = Date.now();
  const existing = args.id ? await db.get("player_groups", args.id) : undefined;
  const group: PlayerGroup = existing
    ? { ...existing, name: args.name, players: args.players, updatedAt: now, lastUsedAt: now }
    : {
        id: args.id ?? `pg_${now.toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
        name: args.name,
        players: args.players,
        createdAt: now,
        updatedAt: now,
        lastUsedAt: now,
      };
  await db.put("player_groups", group);
  return group;
}

export async function touchPlayerGroup(id: string): Promise<void> {
  const db = await getDB();
  const g = await db.get("player_groups", id);
  if (!g) return;
  g.lastUsedAt = Date.now();
  await db.put("player_groups", g);
}

export async function deletePlayerGroup(id: string): Promise<void> {
  const db = await getDB();
  await db.delete("player_groups", id);
}
